import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  TouristSite, TourQuestionnaire, Settings, Location
} from '../../../types';
import {
  CheckCircle2, Star, Clock, Accessibility, Route, PawPrint, Ban, Baby,
  UtensilsCrossed, Utensils, Coffee, Home, RotateCcw, Edit3, Check,
  ChevronRight, AlertTriangle, Lock, Unlock, Info, MapPin, Moon, Sun,
  Waves
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { getDistance, getTravelTime, timeToMinutes, calculateIdealDays } from '../../../services/circuitEngine';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DayPlan {
  dayNum: number;
  sites: TouristSite[];
  lunchChoice: string | null; // site id, 'picnic', or null
  nightChoice: 'return' | 'hotel' | null;
  accommodationId: string | null;
  dinnerChoice: string | null; // site id, 'picnic', or null
}

interface PlanningState {
  days: DayPlan[];
  outOfPlan: TouristSite[]; // activities that don't fit
  chosenDayCount: number;
  isValidated: boolean; // programme validé → repas/hébergement activés
  isLocked: boolean; // après "Continuer" confirmé
  lockedSiteIds: string[]; // sites verrouillés par le client
}

const ORDINAL = ['1ère', '2ème', '3ème', '4ème', '5ème', '6ème', '7ème', '8ème', '9ème', '10ème'];

const CATEGORY_LIMITS: Record<string, number> = {
  baignade: 1,
  grotte: 1,
  terroir: 1,
  activites: 1,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const addMinutes = (time: string, minutes: number): string => {
  const [h, m] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(h, m, 0, 0);
  date.setMinutes(date.getMinutes() + Math.round(minutes));
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const isCateringSite = (site: TouristSite): boolean =>
  site.isCatering === true ||
  (site.categories || []).some(c => c.toLowerCase() === 'restauration') ||
  site.cateringType === 'restaurant' ||
  site.cateringType === 'snack';

const needsLunch = (departureTime: string): boolean =>
  timeToMinutes(departureTime) < timeToMinutes('12:00');

const getLunchWindow = (questionnaire: TourQuestionnaire): { start: string; end: string } => {
  const hasChildrenUnder6 =
    (questionnaire.passengers?.children3_6 || 0) > 0 ||
    (questionnaire.passengers?.babies || 0) > 0;
  return hasChildrenUnder6 ? { start: '11:30', end: '13:00' } : { start: '12:00', end: '14:00' };
};

// Compute start times for each site in a day
const computeDayTimeline = (
  sites: TouristSite[],
  departureTime: string,
  startLoc: { lat: number; lng: number },
  settings: Settings,
  lunchWindow: { start: string; end: string },
  lunchNeeded: boolean,
  lunchDuration: number
): { siteId: string; startTime: string; endTime: string; travelBefore: number }[] => {
  const result: { siteId: string; startTime: string; endTime: string; travelBefore: number }[] = [];
  let currentTime = departureTime;
  let lastLoc = startLoc;
  let mealInserted = false;

  for (const site of sites) {
    const dist = getDistance(lastLoc, site.location);
    const travel = getTravelTime(dist);
    const arrival = addMinutes(currentTime, travel);

    if (lunchNeeded && !mealInserted && timeToMinutes(arrival) >= timeToMinutes(lunchWindow.start)) {
      mealInserted = true;
      currentTime = addMinutes(arrival, lunchDuration);
    }

    const recalcDist = getDistance(lastLoc, site.location);
    const recalcTravel = getTravelTime(recalcDist);
    const start = addMinutes(currentTime, recalcTravel);
    const duration = site.durationMinutes || 60;
    const end = addMinutes(start, duration);

    result.push({ siteId: site.id, startTime: start, endTime: end, travelBefore: recalcTravel });
    currentTime = addMinutes(end, settings.bufferTimeMinutes || 15);
    lastLoc = site.location;
  }

  if (lunchNeeded && !mealInserted) {
    // lunch after all sites
  }

  return result;
};

// Compute how many minutes of a day are used
const computeDayUsedMinutes = (
  sites: TouristSite[],
  departureTime: string,
  settings: Settings,
  lunchNeeded: boolean,
  lunchDuration: number
): number => {
  let mins = 0;
  for (const s of sites) {
    mins += (s.durationMinutes || 60) + (settings.bufferTimeMinutes || 15);
  }
  if (lunchNeeded) mins += lunchDuration;
  return mins;
};

// ─── System optimizer: initial smart assignment ───────────────────────────────

const buildOptimizedPlan = (
  selectedSites: TouristSite[],
  dayCount: number,
  questionnaire: TourQuestionnaire,
  settings: Settings,
  getSiteScore: (site: TouristSite) => { score: number; forcedColor: string | null }
): { days: TouristSite[][]; outOfPlan: TouristSite[] } => {
  const maxDayMins = (settings.tourMaxDurationFullDay || 10) * 60 + 30;
  const lunchNeeded = needsLunch(questionnaire.departureTime);
  const lunchDur = settings.lunchDurationMinutes || 90;
  const buffer = settings.bufferTimeMinutes || 15;

  // Sort sites by priority:
  // 1. isEmblematic (admin flag — stored as isEmblematic on TouristSite)
  // 2. score (green > yellow > orange > red)
  // 3. rating
  const sorted = [...selectedSites].sort((a, b) => {
    const aEmb = (a as any).isEmblematic ? 1 : 0;
    const bEmb = (b as any).isEmblematic ? 1 : 0;
    if (aEmb !== bEmb) return bEmb - aEmb;
    const aScore = getSiteScore(a).score;
    const bScore = getSiteScore(b).score;
    if (aScore !== bScore) return bScore - aScore;
    return (b.rating || b.googleRating || 0) - (a.rating || a.googleRating || 0);
  });

  const days: TouristSite[][] = Array.from({ length: dayCount }, () => []);
  const outOfPlan: TouristSite[] = [];

  // Category tracking per day
  const dayCategoryCount: Record<number, Record<string, number>> = {};
  for (let d = 0; d < dayCount; d++) dayCategoryCount[d] = {};

  const getDayMins = (dayIdx: number) => {
    const sites = days[dayIdx];
    let m = 0;
    for (const s of sites) m += (s.durationMinutes || 60) + buffer;
    if (lunchNeeded) m += lunchDur;
    return m;
  };

  for (const site of sorted) {
    const siteCats = (site.categories || []).filter(c => c in CATEGORY_LIMITS);
    let placed = false;

    // Try to place in a day that:
    // 1. Has room
    // 2. Doesn't violate category limits
    // Prefer earlier days for baignade/grotte/terroir/activites
    for (let d = 0; d < dayCount; d++) {
      const dayMins = getDayMins(d);
      if (dayMins + (site.durationMinutes || 60) + buffer > maxDayMins) continue;

      const catOk = siteCats.every(cat => {
        const limit = CATEGORY_LIMITS[cat] || Infinity;
        const current = dayCategoryCount[d][cat] || 0;
        return current < limit;
      });
      if (!catOk) continue;

      days[d].push(site);
      siteCats.forEach(cat => {
        dayCategoryCount[d][cat] = (dayCategoryCount[d][cat] || 0) + 1;
      });
      placed = true;
      break;
    }

    if (!placed) outOfPlan.push(site);
  }

  // Optimize order within each day (nearest neighbor)
  const startLoc = questionnaire.pickupLocation || { lat: 0, lng: 0 };
  for (let d = 0; d < dayCount; d++) {
    if (days[d].length <= 1) continue;
    const optimized: TouristSite[] = [];
    const remaining = [...days[d]];
    let currentLoc = startLoc;

    while (remaining.length > 0) {
      let bestIdx = 0;
      let bestScore = Infinity;
      for (let i = 0; i < remaining.length; i++) {
        const dist = getDistance(currentLoc, remaining[i].location);
        const travel = getTravelTime(dist);
        let score = travel;
        // Baignade not before 11h
        const isBaignade = (remaining[i].categories || []).includes('baignade');
        if (isBaignade) score += 5000; // prefer afternoon
        if (score < bestScore) { bestScore = score; bestIdx = i; }
      }
      optimized.push(remaining[bestIdx]);
      currentLoc = remaining[bestIdx].location;
      remaining.splice(bestIdx, 1);
    }
    days[d] = optimized;
  }

  return { days, outOfPlan };
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface Step10_1Props {
  questionnaire: TourQuestionnaire;
  setQuestionnaire: React.Dispatch<React.SetStateAction<TourQuestionnaire>>;
  sites: TouristSite[];
  settings: Settings;
  idealDays: number;
  handleBack: () => void;
  onComplete: (circuit?: any) => void;
  getSiteScore: (site: TouristSite) => { score: number; reasons: string[]; isUnsuitable: boolean; forcedColor: 'green' | 'yellow' | 'red' | 'orange' | null };
  getScoreColor: (color: string | null) => string;
  getScoreBadge: (color: string | null) => React.ReactNode;
  setSelectedActivity: (site: TouristSite | null) => void;
  setPreviousStep: (step: number) => void;
  setStep: (step: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const Step10_1: React.FC<Step10_1Props> = ({
  questionnaire,
  setQuestionnaire,
  sites,
  settings,
  idealDays,
  handleBack,
  onComplete,
  getSiteScore,
  getScoreColor,
  getScoreBadge,
  setSelectedActivity,
  setPreviousStep,
  setStep,
}) => {
  const selectedSites = sites.filter(s => questionnaire.selectedActivities.includes(s.id));
  const lunchNeeded = needsLunch(questionnaire.departureTime);
  const lunchWindow = getLunchWindow(questionnaire);
  const lunchDuration = settings.lunchDurationMinutes || 90;
  const picnicDuration = settings.picnicDurationMinutes || 45;
  const maxDayMins = (settings.tourMaxDurationFullDay || 10) * 60 + 30;
  const startLoc = questionnaire.pickupLocation || { lat: 0, lng: 0 };

  // ─── Planning state ────────────────────────────────────────────────────────

  const buildInitialPlan = useCallback((dayCount: number): PlanningState => {
    const { days, outOfPlan } = buildOptimizedPlan(selectedSites, dayCount, questionnaire, settings, getSiteScore);
    return {
      days: days.map((sitesForDay, i) => ({
        dayNum: i + 1,
        sites: sitesForDay,
        lunchChoice: null,
        nightChoice: null,
        accommodationId: null,
        dinnerChoice: null,
      })),
      outOfPlan,
      chosenDayCount: dayCount,
      isValidated: false,
      isLocked: false,
      lockedSiteIds: [],
    };
  }, [selectedSites, questionnaire, settings, getSiteScore]);

  const [plan, setPlan] = useState<PlanningState>(() => buildInitialPlan(idealDays));
  const [chosenDays, setChosenDays] = useState(idealDays);

  // ─── Meal/hotel panels ─────────────────────────────────────────────────────
  const [openLunchPanel, setOpenLunchPanel] = useState<number | null>(null); // dayNum
  const [openDinnerPanel, setOpenDinnerPanel] = useState<number | null>(null);
  const [openAccommoPanel, setOpenAccommoPanel] = useState<number | null>(null);
  const [lunchFilter, setLunchFilter] = useState<'all' | 'snack' | 1 | 2 | 3>('all');
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmContinue, setConfirmContinue] = useState(false);
  const [missingItems, setMissingItems] = useState<string[]>([]);

  // ─── Drag & drop ──────────────────────────────────────────────────────────
  const [dragging, setDragging] = useState<{ siteId: string; fromDay: number } | null>(null);
  const [dragOverDay, setDragOverDay] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingRef = useRef(false);

  // ─── Change day count ──────────────────────────────────────────────────────
  const handleDayCountChange = (count: number) => {
    setChosenDays(count);
    setPlan(buildInitialPlan(count));
    setOpenLunchPanel(null);
    setOpenDinnerPanel(null);
    setOpenAccommoPanel(null);
  };

  // ─── Day time usage ────────────────────────────────────────────────────────
  const getDayUsedMins = (dayPlan: DayPlan): number =>
    computeDayUsedMinutes(dayPlan.sites, questionnaire.departureTime, settings, lunchNeeded, lunchDuration);

  const getDayUsedPercent = (dayPlan: DayPlan): number =>
    Math.min(100, Math.round((getDayUsedMins(dayPlan) / maxDayMins) * 100));

  // ─── Drag handlers (touch) ────────────────────────────────────────────────
  const handleTouchStart = (siteId: string, fromDay: number) => {
    if (!plan.isValidated || plan.isLocked) return;
    longPressTimer.current = setTimeout(() => {
      isDraggingRef.current = true;
      setDragging({ siteId, fromDay });
      if (navigator.vibrate) navigator.vibrate(40);
    }, 400);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDraggingRef.current) {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      return;
    }
    e.preventDefault();
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const dayEl = el?.closest('[data-day-num]');
    const idxEl = el?.closest('[data-site-idx]');
    if (dayEl) setDragOverDay(parseInt(dayEl.getAttribute('data-day-num') || '-1'));
    if (idxEl) setDragOverIdx(parseInt(idxEl.getAttribute('data-site-idx') || '-1'));
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    if (isDraggingRef.current && dragging && dragOverDay !== null) {
      moveSite(dragging.siteId, dragging.fromDay, dragOverDay, dragOverIdx ?? -1);
    }
    isDraggingRef.current = false;
    setDragging(null);
    setDragOverDay(null);
    setDragOverIdx(null);
  };

  const moveSite = (siteId: string, fromDay: number, toDay: number, toIdx: number) => {
    const site = selectedSites.find(s => s.id === siteId);
    if (!site || plan.lockedSiteIds.includes(siteId)) return;

    setPlan(prev => {
      const newDays = prev.days.map(d => ({ ...d, sites: [...d.sites] }));

      // Remove from source
      const fromDayPlan = newDays.find(d => d.dayNum === fromDay);
      const toDayPlan = newDays.find(d => d.dayNum === toDay);
      if (!fromDayPlan || !toDayPlan) return prev;

      fromDayPlan.sites = fromDayPlan.sites.filter(s => s.id !== siteId);

      // Insert at target position
      if (toIdx < 0 || toIdx >= toDayPlan.sites.length) {
        toDayPlan.sites.push(site);
      } else {
        toDayPlan.sites.splice(toIdx, 0, site);
      }

      // Check compatibility: invalidate lunch/dinner if moved from that day
      if (fromDay !== toDay) {
        // Reset lunch of fromDay if the day changed significantly
        fromDayPlan.lunchChoice = null;
        // Reset dinner/accommodation if toDay already had accommodation
        if (toDayPlan.accommodationId) {
          const accSite = sites.find(s => s.id === toDayPlan.accommodationId);
          if (accSite) {
            const lastSite = toDayPlan.sites[toDayPlan.sites.length - 1];
            if (lastSite) {
              const dist = getDistance(lastSite.location, accSite.location);
              if (dist > 10) {
                toDayPlan.accommodationId = null;
                toDayPlan.dinnerChoice = null;
              }
            }
          }
        }
      }

      return { ...prev, days: newDays };
    });
  };

  // ─── Validate programme ────────────────────────────────────────────────────
  const handleValidate = () => {
    setPlan(prev => ({ ...prev, isValidated: true }));
  };

  // ─── Modifier ─────────────────────────────────────────────────────────────
  const handleModifier = () => {
    setPlan(prev => {
      const newDays = prev.days.map(d => {
        // Keep lunch/dinner/accommodation only if still compatible
        // (simplified: keep all — full compatibility check on drag)
        return { ...d };
      });
      return { ...prev, isValidated: false, days: newDays };
    });
  };

  // ─── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setPlan(buildInitialPlan(chosenDays));
    setConfirmReset(false);
    setOpenLunchPanel(null);
    setOpenDinnerPanel(null);
    setOpenAccommoPanel(null);
  };

  // ─── Toggle lock ──────────────────────────────────────────────────────────
  const toggleLock = (siteId: string) => {
    setPlan(prev => ({
      ...prev,
      lockedSiteIds: prev.lockedSiteIds.includes(siteId)
        ? prev.lockedSiteIds.filter(id => id !== siteId)
        : [...prev.lockedSiteIds, siteId],
    }));
  };

  // ─── Lunch selection ──────────────────────────────────────────────────────
  const getLunchOptions = (dayNum: number): TouristSite[] => {
    const dayPlan = plan.days.find(d => d.dayNum === dayNum);
    if (!dayPlan) return [];
    const lastSite = dayPlan.sites[dayPlan.sites.length - 1];
    const refLoc = lastSite?.location || startLoc;

    return sites.filter(s => {
      if (!isCateringSite(s)) return false;
      if (s.sector !== questionnaire.sector) return false;
      const dist = getDistance(refLoc, s.location);
      if (dist > 15) return false;
      if (lunchFilter === 'snack') return (s.forks ?? 0) === 0;
      if (typeof lunchFilter === 'number') return (s.forks ?? 0) === lunchFilter;
      return true;
    }).sort((a, b) => getDistance(refLoc, a.location) - getDistance(refLoc, b.location));
  };

  const setLunch = (dayNum: number, choice: string) => {
    setPlan(prev => ({
      ...prev,
      days: prev.days.map(d => d.dayNum === dayNum ? { ...d, lunchChoice: choice } : d),
    }));
    setOpenLunchPanel(null);
  };

  // ─── Accommodation ────────────────────────────────────────────────────────
  const getAccommOptions = (dayNum: number): TouristSite[] => {
    const dayPlan = plan.days.find(d => d.dayNum === dayNum);
    if (!dayPlan) return [];
    const lastSite = dayPlan.sites[dayPlan.sites.length - 1];
    const refLoc = lastSite?.location || startLoc;

    return sites.filter(s => {
      if (!s.isAccommodation) return false;
      if (s.sector !== questionnaire.sector) return false;
      const dist = getDistance(refLoc, s.location);
      return dist <= 20;
    }).sort((a, b) => {
      const da = getDistance(refLoc, a.location);
      const db = getDistance(refLoc, b.location);
      return da - db;
    });
  };

  const setAccommo = (dayNum: number, siteId: string) => {
    setPlan(prev => ({
      ...prev,
      days: prev.days.map(d => d.dayNum === dayNum
        ? { ...d, accommodationId: siteId, dinnerChoice: null }
        : d),
    }));
    setOpenAccommoPanel(null);
  };

  // ─── Dinner ───────────────────────────────────────────────────────────────
  const getDinnerOptions = (dayNum: number): TouristSite[] => {
    const dayPlan = plan.days.find(d => d.dayNum === dayNum);
    if (!dayPlan) return [];
    const accSite = dayPlan.accommodationId ? sites.find(s => s.id === dayPlan.accommodationId) : null;
    const refLoc = accSite?.location || dayPlan.sites[dayPlan.sites.length - 1]?.location || startLoc;

    const options = sites.filter(s => {
      if (!isCateringSite(s)) return false;
      if (s.sector !== questionnaire.sector) return false;
      const dist = getDistance(refLoc, s.location);
      return dist <= 10;
    }).sort((a, b) => {
      // Accommodation with catering first
      if (a.id === dayPlan.accommodationId) return -1;
      if (b.id === dayPlan.accommodationId) return 1;
      return getDistance(refLoc, a.location) - getDistance(refLoc, b.location);
    });

    return options;
  };

  const setDinner = (dayNum: number, choice: string) => {
    setPlan(prev => ({
      ...prev,
      days: prev.days.map(d => d.dayNum === dayNum ? { ...d, dinnerChoice: choice } : d),
    }));
    setOpenDinnerPanel(null);
  };

  // ─── Completeness check ───────────────────────────────────────────────────
  const getMissingItems = (): string[] => {
    const missing: string[] = [];
    plan.days.forEach((day, i) => {
      const ordinal = ORDINAL[i] || `${i + 1}ème`;
      if (lunchNeeded && !day.lunchChoice) {
        missing.push(`Repas de midi — ${ordinal} journée`);
      }
      const isLastDay = i === plan.days.length - 1;
      if (!isLastDay) {
        if (!day.nightChoice) {
          missing.push(`Hébergement — nuit ${i + 1}`);
        } else if (day.nightChoice === 'hotel' && !day.accommodationId) {
          missing.push(`Choix d'hébergement — nuit ${i + 1}`);
        } else if (day.nightChoice === 'hotel' && day.accommodationId && !day.dinnerChoice) {
          missing.push(`Repas du soir — nuit ${i + 1}`);
        }
      }
    });
    return missing;
  };

  // ─── Continue ─────────────────────────────────────────────────────────────
  const handleContinueAttempt = () => {
    const missing = getMissingItems();
    if (missing.length > 0) {
      setMissingItems(missing);
      return;
    }
    setMissingItems([]);
    setConfirmContinue(true);
  };

  const handleConfirmContinue = () => {
    // Build questionnaire update from plan
    const selectedRestaurants: Record<number, { lunch?: string; dinner?: string }> = {};
    const selectedAccommodations: Record<number, string> = {};
    const accommodationByNight: Record<number, 'return' | 'hotel'> = {};

    plan.days.forEach((day, i) => {
      const dayNum = i + 1;
      selectedRestaurants[dayNum] = {};
      if (day.lunchChoice) selectedRestaurants[dayNum].lunch = day.lunchChoice;
      if (day.dinnerChoice) selectedRestaurants[dayNum].dinner = day.dinnerChoice;
      if (day.accommodationId) selectedAccommodations[dayNum] = day.accommodationId;
      if (day.nightChoice) accommodationByNight[dayNum] = day.nightChoice;
    });

    setQuestionnaire(prev => ({
      ...prev,
      selectedRestaurants,
      selectedAccommodations,
      accommodationByNight,
      logistics: { ...prev.logistics, days: plan.days.length },
    }));

    onComplete();
  };

  // ─── Site card ─────────────────────────────────────────────────────────────
  const SiteCard = ({
    site,
    dayNum,
    siteIdx,
    isOutOfPlan = false,
  }: {
    site: TouristSite;
    dayNum: number;
    siteIdx: number;
    isOutOfPlan?: boolean;
  }) => {
    const score = getSiteScore(site);
    const isLocked = plan.lockedSiteIds.includes(site.id);
    const isDraggingThis = dragging?.siteId === site.id;

    return (
      <div
        data-site-idx={siteIdx}
        onTouchStart={() => handleTouchStart(site.id, dayNum)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={cn(
          "flex items-stretch gap-2 rounded-xl border transition-all select-none",
          isOutOfPlan
            ? "bg-zinc-900/30 border-zinc-800/40 opacity-60"
            : getScoreColor(score.forcedColor),
          isDraggingThis && "opacity-30 scale-95",
          dragOverDay === dayNum && dragOverIdx === siteIdx && "border-emerald-400 bg-emerald-500/5"
        )}
      >
        {/* Thumbnail */}
        <button
          onClick={() => {
            setSelectedActivity(site);
            setPreviousStep(10.1);
            setStep(10);
          }}
          className="w-16 h-16 my-2 ml-2 rounded-lg bg-zinc-800 overflow-hidden shrink-0 border border-white/10 self-center"
        >
          {site.image && !site.image.includes('maps.googleapis.com') ? (
            <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-lg">📍</div>
          )}
        </button>

        {/* Info */}
        <button
          onClick={() => {
            setSelectedActivity(site);
            setPreviousStep(10.1);
            setStep(10);
          }}
          className="flex-1 min-w-0 py-3 text-left"
        >
          <h3 className="text-white font-bold text-sm leading-snug">{site.name}</h3>
          <p className="text-[10px] text-zinc-400 line-clamp-2 mt-0.5 leading-snug">{site.description}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="flex items-center gap-0.5 text-[10px] text-zinc-500">
              <Clock className="w-3 h-3" />{site.durationMinutes || 60} min
            </span>
            {(site.rating || site.googleRating) && (
              <span className="flex items-center gap-0.5 text-[10px] text-amber-400">
                <Star className="w-3 h-3 fill-current" />{site.rating || site.googleRating}
              </span>
            )}
            {site.isHandicapAccessible && <Accessibility className="w-3 h-3 text-blue-400" />}
            {site.isWindingRoad && <Route className="w-3 h-3 text-amber-400" />}
          </div>
        </button>

        {/* Lock button */}
        {plan.isValidated && !plan.isLocked && !isOutOfPlan && (
          <button
            onClick={() => toggleLock(site.id)}
            className="px-2 flex items-center justify-center text-zinc-600 hover:text-zinc-300 transition-colors shrink-0"
          >
            {isLocked
              ? <Lock className="w-3.5 h-3.5 text-amber-400" />
              : <Unlock className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    );
  };

  // ─── Lunch panel ──────────────────────────────────────────────────────────
  const LunchPanel = ({ dayNum }: { dayNum: number }) => {
    const options = getLunchOptions(dayNum);
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950/95 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="text-white font-bold">Repas de midi — Jour {dayNum}</h3>
          <button onClick={() => setOpenLunchPanel(null)} className="text-zinc-400 hover:text-white">✕</button>
        </div>
        {/* Filters */}
        <div className="flex gap-2 px-4 pt-3 pb-2 overflow-x-auto no-scrollbar">
          {(['all', 'snack', 1, 2, 3] as const).map(f => (
            <button
              key={String(f)}
              onClick={() => setLunchFilter(f)}
              className={cn(
                "px-3 py-1.5 rounded-full text-[10px] font-bold whitespace-nowrap border transition-all",
                lunchFilter === f ? "bg-emerald-500 border-emerald-500 text-white" : "bg-zinc-900 border-zinc-800 text-zinc-400"
              )}
            >
              {f === 'all' ? 'Tous' : f === 'snack' ? 'Snack' : '🍴'.repeat(f as number)}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-24 space-y-2 pt-2">
          {/* Picnic option */}
          <button
            onClick={() => setLunch(dayNum, 'picnic')}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-xl border transition-all",
              plan.days.find(d => d.dayNum === dayNum)?.lunchChoice === 'picnic'
                ? "bg-emerald-500/10 border-emerald-500" : "bg-zinc-900 border-zinc-800"
            )}
          >
            <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center text-2xl shrink-0">🧺</div>
            <div className="flex-1 text-left">
              <h4 className="text-white font-bold text-sm">Pique-nique</h4>
              <p className="text-[10px] text-zinc-400">Repas en plein air · {picnicDuration} min</p>
            </div>
            {plan.days.find(d => d.dayNum === dayNum)?.lunchChoice === 'picnic' && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          </button>

          {options.map(site => (
            <button
              key={site.id}
              onClick={() => setLunch(dayNum, site.id)}
              className={cn(
                "w-full flex items-center gap-3 p-3 rounded-xl border transition-all",
                plan.days.find(d => d.dayNum === dayNum)?.lunchChoice === site.id
                  ? "bg-emerald-500/10 border-emerald-500" : "bg-zinc-900 border-zinc-800"
              )}
            >
              <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden shrink-0 border border-white/5">
                {site.image && !site.image.includes('maps.googleapis.com')
                  ? <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  : <div className="w-full h-full flex items-center justify-center">🍽️</div>}
              </div>
              <div className="flex-1 text-left min-w-0">
                <h4 className="text-white font-bold text-sm truncate">{site.name}</h4>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-zinc-500">{(site.forks ?? 0) === 0 ? 'Snack' : '🍴'.repeat(site.forks ?? 0)}</span>
                  {(site.rating || site.googleRating) && (
                    <span className="flex items-center gap-0.5 text-[10px] text-amber-400">
                      <Star className="w-3 h-3 fill-current" />{site.rating || site.googleRating}
                    </span>
                  )}
                </div>
              </div>
              {plan.days.find(d => d.dayNum === dayNum)?.lunchChoice === site.id && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
            </button>
          ))}

          {options.length === 0 && (
            <p className="text-zinc-500 text-sm text-center p-8">Aucun établissement trouvé à proximité.</p>
          )}
        </div>
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-zinc-950/95 border-t border-zinc-800">
          <button
            onClick={() => setOpenLunchPanel(null)}
            disabled={!plan.days.find(d => d.dayNum === dayNum)?.lunchChoice}
            className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
          >
            Valider mon choix
          </button>
        </div>
      </div>
    );
  };

  // ─── Accommodation panel ──────────────────────────────────────────────────
  const AccommoPanel = ({ dayNum }: { dayNum: number }) => {
    const options = getAccommOptions(dayNum);
    const dayPlan = plan.days.find(d => d.dayNum === dayNum);
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950/95 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="text-white font-bold">Hébergement — Nuit {dayNum}</h3>
          <button onClick={() => setOpenAccommoPanel(null)} className="text-zinc-400 hover:text-white">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-24 space-y-2 pt-3">
          {options.map(site => (
            <button
              key={site.id}
              onClick={() => setAccommo(dayNum, site.id)}
              className={cn(
                "w-full flex items-center gap-3 p-3 rounded-xl border transition-all",
                dayPlan?.accommodationId === site.id ? "bg-emerald-500/10 border-emerald-500" : "bg-zinc-900 border-zinc-800"
              )}
            >
              <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden shrink-0 border border-white/5">
                {site.image && !site.image.includes('maps.googleapis.com')
                  ? <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  : <div className="w-full h-full flex items-center justify-center">🏡</div>}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-white font-bold text-sm truncate">{site.name}</h4>
                  {site.isCatering && <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-bold shrink-0">Restauration</span>}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {(site.rating || site.googleRating) && (
                    <span className="flex items-center gap-0.5 text-[10px] text-amber-400">
                      <Star className="w-3 h-3 fill-current" />{site.rating || site.googleRating}
                    </span>
                  )}
                  <span className="text-[10px] text-zinc-500">à {getDistance(dayPlan?.sites[dayPlan.sites.length - 1]?.location || startLoc, site.location).toFixed(1)} km</span>
                </div>
              </div>
              {dayPlan?.accommodationId === site.id && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
            </button>
          ))}
          {options.length === 0 && <p className="text-zinc-500 text-sm text-center p-8">Aucun hébergement trouvé à proximité.</p>}
        </div>
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-zinc-950/95 border-t border-zinc-800">
          <button
            onClick={() => setOpenAccommoPanel(null)}
            disabled={!dayPlan?.accommodationId}
            className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
          >
            Valider l'hébergement
          </button>
        </div>
      </div>
    );
  };

  // ─── Dinner panel ─────────────────────────────────────────────────────────
  const DinnerPanel = ({ dayNum }: { dayNum: number }) => {
    const options = getDinnerOptions(dayNum);
    const dayPlan = plan.days.find(d => d.dayNum === dayNum);
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950/95 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="text-white font-bold">Repas du soir — Nuit {dayNum}</h3>
          <button onClick={() => setOpenDinnerPanel(null)} className="text-zinc-400 hover:text-white">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-24 space-y-2 pt-3">
          {/* Picnic */}
          <button
            onClick={() => setDinner(dayNum, 'picnic')}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-xl border transition-all",
              dayPlan?.dinnerChoice === 'picnic' ? "bg-emerald-500/10 border-emerald-500" : "bg-zinc-900 border-zinc-800"
            )}
          >
            <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center text-2xl shrink-0">🧺</div>
            <div className="flex-1 text-left">
              <h4 className="text-white font-bold text-sm">Pique-nique / Repas libre</h4>
            </div>
            {dayPlan?.dinnerChoice === 'picnic' && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          </button>

          {options.map(site => (
            <button
              key={site.id}
              onClick={() => setDinner(dayNum, site.id)}
              className={cn(
                "w-full flex items-center gap-3 p-3 rounded-xl border transition-all",
                dayPlan?.dinnerChoice === site.id ? "bg-emerald-500/10 border-emerald-500" :
                site.id === dayPlan?.accommodationId ? "bg-blue-500/5 border-blue-500/30" : "bg-zinc-900 border-zinc-800"
              )}
            >
              <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden shrink-0 border border-white/5">
                {site.image && !site.image.includes('maps.googleapis.com')
                  ? <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  : <div className="w-full h-full flex items-center justify-center">🍽️</div>}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-white font-bold text-sm truncate">{site.name}</h4>
                  {site.id === dayPlan?.accommodationId && <span className="text-[9px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded font-bold shrink-0">Votre logement</span>}
                </div>
              </div>
              {dayPlan?.dinnerChoice === site.id && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
            </button>
          ))}
        </div>
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-zinc-950/95 border-t border-zinc-800">
          <button
            onClick={() => setOpenDinnerPanel(null)}
            disabled={!dayPlan?.dinnerChoice}
            className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
          >
            Valider mon choix
          </button>
        </div>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[100dvh] bg-zinc-950 overflow-hidden">

      {/* Panels */}
      {openLunchPanel !== null && <LunchPanel dayNum={openLunchPanel} />}
      {openAccommoPanel !== null && <AccommoPanel dayNum={openAccommoPanel} />}
      {openDinnerPanel !== null && <DinnerPanel dayNum={openDinnerPanel} />}

      {/* Day selector */}
      <div className="shrink-0 px-4 pt-4 pb-2">
        <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest mb-2">Durée du séjour</p>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {Array.from({ length: idealDays }, (_, i) => i + 1).map(d => (
            <button
              key={d}
              onClick={() => handleDayCountChange(d)}
              className={cn(
                "px-4 py-2 rounded-xl border font-bold text-sm whitespace-nowrap transition-all shrink-0",
                chosenDays === d
                  ? "bg-emerald-500 border-emerald-500 text-white shadow-[0_0_12px_rgba(16,185,129,0.4)]"
                  : d === idealDays
                    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-400"
                    : "bg-zinc-900 border-zinc-800 text-zinc-400"
              )}
            >
              {d === 1 ? '1 jour' : `${d} jours`}
              {d === idealDays && <span className="ml-1 text-[9px] opacity-70">✓ idéal</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 pb-32 space-y-6 pt-2">
        {plan.days.map((dayPlan, dayIdx) => {
          const ordinal = ORDINAL[dayIdx] || `${dayIdx + 1}ème`;
          const usedPercent = getDayUsedPercent(dayPlan);
          const isLastDay = dayIdx === plan.days.length - 1;

          return (
            <div key={dayPlan.dayNum} data-day-num={dayPlan.dayNum} className="space-y-2">
              {/* Day header + gauge */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest">
                  {ordinal} journée
                </span>
                <span className="text-[10px] text-zinc-500">{usedPercent}% du temps</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={cn("h-full rounded-full transition-all", usedPercent > 90 ? "bg-red-500" : usedPercent > 70 ? "bg-amber-500" : "bg-emerald-500")}
                  style={{ width: `${usedPercent}%` }}
                />
              </div>

              {/* Sites */}
              <div className="space-y-2">
                {dayPlan.sites.map((site, siteIdx) => (
                  <SiteCard key={site.id} site={site} dayNum={dayPlan.dayNum} siteIdx={siteIdx} />
                ))}
                {dayPlan.sites.length === 0 && (
                  <div className="p-4 text-center text-zinc-600 text-xs border border-dashed border-zinc-800 rounded-xl">
                    Glissez des activités ici
                  </div>
                )}
              </div>

              {/* Lunch vignette */}
              {lunchNeeded && (
                <button
                  onClick={() => plan.isValidated ? setOpenLunchPanel(dayPlan.dayNum) : null}
                  disabled={!plan.isValidated}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left",
                    !plan.isValidated
                      ? "bg-zinc-900/30 border-zinc-800/40 opacity-60 cursor-not-allowed"
                      : dayPlan.lunchChoice
                        ? "bg-amber-500/10 border-amber-500/40"
                        : "bg-zinc-900 border-zinc-700 hover:border-amber-500/50"
                  )}
                >
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-xl shrink-0">
                    {dayPlan.lunchChoice === 'picnic' ? '🧺' : dayPlan.lunchChoice ? '🍽️' : '🍴'}
                  </div>
                  <div className="flex-1 min-w-0">
                    {dayPlan.lunchChoice
                      ? <p className="text-sm font-bold text-white">
                          {dayPlan.lunchChoice === 'picnic' ? 'Pique-nique' : sites.find(s => s.id === dayPlan.lunchChoice)?.name || 'Repas choisi'}
                        </p>
                      : !plan.isValidated
                        ? <>
                            <p className="text-sm font-bold text-zinc-400">Pause repas de midi</p>
                            <p className="text-[10px] text-zinc-600">Validez votre programme pour choisir votre repas</p>
                          </>
                        : <p className="text-sm font-bold text-amber-400">Choisissez votre repas de midi</p>
                    }
                  </div>
                  {plan.isValidated && <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                </button>
              )}

              {/* Night vignette (not last day) */}
              {!isLastDay && (
                <div className="space-y-2">
                  {/* Night choice question */}
                  {(!dayPlan.nightChoice || !plan.isValidated) && (
                    <div className="p-3 rounded-xl border bg-zinc-900/60 border-zinc-700 space-y-2">
                      <div className="flex items-center gap-2">
                        <Moon className="w-4 h-4 text-blue-400" />
                        <p className="text-sm font-bold text-white">Dormir sur place ou rentrer ?</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setPlan(prev => ({
                            ...prev,
                            days: prev.days.map(d => d.dayNum === dayPlan.dayNum
                              ? { ...d, nightChoice: 'return', accommodationId: null, dinnerChoice: null }
                              : d)
                          }))}
                          className={cn(
                            "py-2 rounded-lg border text-xs font-semibold transition-all",
                            dayPlan.nightChoice === 'return'
                              ? "bg-zinc-700 border-zinc-600 text-white"
                              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600"
                          )}
                        >
                          <Sun className="w-3.5 h-3.5 mx-auto mb-0.5" />
                          Rentrer
                        </button>
                        <button
                          onClick={() => setPlan(prev => ({
                            ...prev,
                            days: prev.days.map(d => d.dayNum === dayPlan.dayNum
                              ? { ...d, nightChoice: 'hotel' }
                              : d)
                          }))}
                          className={cn(
                            "py-2 rounded-lg border text-xs font-semibold transition-all",
                            dayPlan.nightChoice === 'hotel'
                              ? "bg-blue-500/20 border-blue-500 text-blue-300"
                              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600"
                          )}
                        >
                          <Moon className="w-3.5 h-3.5 mx-auto mb-0.5" />
                          Sur place
                        </button>
                      </div>
                    </div>
                  )}

                  {/* If return: reduced grayed vignette */}
                  {dayPlan.nightChoice === 'return' && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-zinc-900/30 border border-zinc-800/40">
                      <Sun className="w-3.5 h-3.5 text-zinc-600" />
                      <p className="text-[11px] text-zinc-600">Retour au lieu de séjour</p>
                      <button
                        onClick={() => setPlan(prev => ({
                          ...prev,
                          days: prev.days.map(d => d.dayNum === dayPlan.dayNum ? { ...d, nightChoice: null } : d)
                        }))}
                        className="ml-auto text-[10px] text-zinc-600 hover:text-zinc-400"
                      >
                        Modifier
                      </button>
                    </div>
                  )}

                  {/* If hotel: accommodation vignette */}
                  {dayPlan.nightChoice === 'hotel' && (
                    <button
                      onClick={() => plan.isValidated ? setOpenAccommoPanel(dayPlan.dayNum) : null}
                      disabled={!plan.isValidated}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left",
                        !plan.isValidated
                          ? "bg-zinc-900/30 border-zinc-800/40 opacity-60 cursor-not-allowed"
                          : dayPlan.accommodationId
                            ? "bg-blue-500/10 border-blue-500/40"
                            : "bg-zinc-900 border-zinc-700 hover:border-blue-500/50"
                      )}
                    >
                      <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-xl shrink-0">🏡</div>
                      <div className="flex-1 min-w-0">
                        {dayPlan.accommodationId
                          ? <p className="text-sm font-bold text-white">{sites.find(s => s.id === dayPlan.accommodationId)?.name}</p>
                          : !plan.isValidated
                            ? <>
                                <p className="text-sm font-bold text-zinc-400">Hébergement</p>
                                <p className="text-[10px] text-zinc-600">Validez votre programme pour choisir votre hébergement</p>
                              </>
                            : <p className="text-sm font-bold text-blue-400">Choisissez votre hébergement</p>
                        }
                      </div>
                      {plan.isValidated && <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                    </button>
                  )}

                  {/* Dinner vignette (only after accommodation chosen and plan validated) */}
                  {dayPlan.nightChoice === 'hotel' && dayPlan.accommodationId && plan.isValidated && (
                    <button
                      onClick={() => setOpenDinnerPanel(dayPlan.dayNum)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left",
                        dayPlan.dinnerChoice
                          ? "bg-amber-500/10 border-amber-500/40"
                          : "bg-zinc-900 border-zinc-700 hover:border-amber-500/50"
                      )}
                    >
                      <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-xl shrink-0">
                        {dayPlan.dinnerChoice === 'picnic' ? '🧺' : '🌙'}
                      </div>
                      <div className="flex-1 min-w-0">
                        {dayPlan.dinnerChoice
                          ? <p className="text-sm font-bold text-white">
                              {dayPlan.dinnerChoice === 'picnic' ? 'Pique-nique / Repas libre' : sites.find(s => s.id === dayPlan.dinnerChoice)?.name || 'Repas du soir choisi'}
                            </p>
                          : <p className="text-sm font-bold text-amber-400">Choisissez votre repas du soir</p>
                        }
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Out of plan */}
        {plan.outOfPlan.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-zinc-700" />
              <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest shrink-0">
                Non incluses dans ce programme
              </span>
              <div className="flex-1 h-px bg-zinc-700" />
            </div>
            {plan.outOfPlan.map((site, i) => (
              <SiteCard key={site.id} site={site} dayNum={-1} siteIdx={i} isOutOfPlan />
            ))}
          </div>
        )}
      </div>

      {/* Missing items message */}
      {missingItems.length > 0 && (
        <div className="mx-4 mb-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <p className="text-xs font-bold text-amber-400 mb-1">Il manque :</p>
          {missingItems.map((item, i) => (
            <p key={i} className="text-[11px] text-amber-300">• {item}</p>
          ))}
        </div>
      )}

      {/* Confirm reset modal */}
      {confirmReset && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 space-y-4 max-w-sm w-full">
            <h3 className="text-white font-bold text-lg">Réinitialiser le planning ?</h3>
            <p className="text-zinc-400 text-sm">Tous vos ajustements, repas et hébergements seront remis à zéro. Le système reprendra son planning optimisé.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmReset(false)} className="flex-1 py-2.5 bg-zinc-800 text-zinc-300 rounded-xl text-sm font-semibold">Annuler</button>
              <button onClick={handleReset} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold">Réinitialiser</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm continue modal */}
      {confirmContinue && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 space-y-4 max-w-sm w-full">
            <h3 className="text-white font-bold text-lg">Confirmer l'itinéraire ?</h3>
            <p className="text-zinc-400 text-sm">Votre programme sera finalisé. Vous pourrez voir le détail du circuit, les distances et les durées à l'étape suivante.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmContinue(false)} className="flex-1 py-2.5 bg-zinc-800 text-zinc-300 rounded-xl text-sm font-semibold">Retour</button>
              <button onClick={handleConfirmContinue} className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold shadow-[0_0_15px_rgba(16,185,129,0.3)]">Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed bottom bar — 4 buttons */}
      <div className="shrink-0 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 px-4 py-3">
        <div className="grid grid-cols-4 gap-2">
          {/* Reset */}
          <button
            onClick={() => setConfirmReset(true)}
            className="flex flex-col items-center gap-1 py-2 px-1 bg-zinc-800 rounded-xl border border-zinc-700 hover:border-zinc-600 transition-all"
          >
            <RotateCcw className="w-4 h-4 text-zinc-400" />
            <span className="text-[9px] text-zinc-400 font-semibold">Reset</span>
          </button>

          {/* Modifier */}
          <button
            onClick={handleModifier}
            disabled={!plan.isValidated}
            className={cn(
              "flex flex-col items-center gap-1 py-2 px-1 rounded-xl border transition-all",
              plan.isValidated
                ? "bg-zinc-800 border-zinc-700 hover:border-zinc-600"
                : "bg-zinc-900/40 border-zinc-800/40 opacity-40 cursor-not-allowed"
            )}
          >
            <Edit3 className="w-4 h-4 text-zinc-400" />
            <span className="text-[9px] text-zinc-400 font-semibold">Modifier</span>
          </button>

          {/* Valider programme */}
          <button
            onClick={handleValidate}
            disabled={plan.isValidated}
            className={cn(
              "flex flex-col items-center gap-1 py-2 px-1 rounded-xl border transition-all",
              !plan.isValidated
                ? "bg-emerald-500/20 border-emerald-500/50 hover:bg-emerald-500/30"
                : "bg-zinc-900/40 border-zinc-800/40 opacity-40 cursor-not-allowed"
            )}
          >
            <Check className="w-4 h-4 text-emerald-400" />
            <span className="text-[9px] text-emerald-400 font-semibold leading-tight text-center">Valider</span>
          </button>

          {/* Continuer */}
          <button
            onClick={handleContinueAttempt}
            className="flex flex-col items-center gap-1 py-2 px-1 bg-emerald-500 rounded-xl border border-emerald-500 hover:bg-emerald-600 transition-all shadow-[0_0_12px_rgba(16,185,129,0.3)]"
          >
            <ChevronRight className="w-4 h-4 text-white" />
            <span className="text-[9px] text-white font-semibold">Continuer</span>
          </button>
        </div>
      </div>
    </div>
  );
};
